import React from 'react';
import { Checkbox } from 'syngenta-digital-cropwise-react-ui-kit';
import FormattedNumber from 'components/FormattedNumber';
import CropIconMapping from 'components/CropIconMapping';
import FieldIcon from 'components/Icons/FieldIcon';
import HighlightedText from 'components/HighlightedText';
import { CropGroup, CropZone, Farm, Field } from './types';
import { getCzArea, pluralize } from './helpers';
import {
  ARCHIVED_COLOR,
  CROP_ZONE_ICON_BORDER,
  CROP_ZONE_ICON_BORDER_ARCHIVED,
  CROP_ZONE_ICON_FILL,
  CROP_ZONE_ICON_FILL_ARCHIVED,
  HIGHLIGHT_COLOR,
  META_COLOR,
  SELECTED_HIGHLIGHT_COLOR
} from './constants';

// ---------------------------------------------------------------------------
// IndeterminateCheckbox
// ---------------------------------------------------------------------------
interface IndeterminateCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  'aria-label'?: string;
}

const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
  checked,
  indeterminate,
  onChange,
  'aria-label': ariaLabel
}) => (
  <Checkbox
    checked={checked}
    indeterminate={indeterminate}
    onChange={onChange}
    aria-label={ariaLabel}
    className="spv2-checkbox shrink-0"
    onClick={e => e.stopPropagation()}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onChange();
      }
    }}
  />
);

// ---------------------------------------------------------------------------
// CropZoneTitleItem
// ---------------------------------------------------------------------------
interface CropZoneTitleItemProps {
  cropZone: CropZone;
  isChecked: boolean;
  onToggle: (id: string) => void;
  geometry?: any;
  searchTerm?: string;
  isCropGroupMode?: boolean;
}

export const CropZoneTitleItem: React.FC<CropZoneTitleItemProps> = React.memo(
  ({
    cropZone,
    isChecked,
    onToggle,
    geometry = null,
    searchTerm = '',
    isCropGroupMode = false
  }) => {
    const area = getCzArea(cropZone);
    const cropName = cropZone.crop?.name ?? '';
    const textColor = cropZone.archived ? ARCHIVED_COLOR : undefined;
    const metaColor = cropZone.archived ? ARCHIVED_COLOR : META_COLOR;
    // Crop zone shape: outlined (fill + border) so it reads as distinct from a
    // field's solid fill. Archived uses a muted pair.
    const iconColor = cropZone.archived
      ? CROP_ZONE_ICON_FILL_ARCHIVED
      : CROP_ZONE_ICON_FILL;
    const iconBorder = cropZone.archived
      ? CROP_ZONE_ICON_BORDER_ARCHIVED
      : CROP_ZONE_ICON_BORDER;

    return (
      <div
        className="spv2-title-btn"
        role="none"
        onClick={e => e.stopPropagation()}
      >
        <IndeterminateCheckbox
          checked={isChecked}
          indeterminate={false}
          onChange={() => onToggle(cropZone.id)}
          aria-label={`Select ${cropZone.name}`}
        />
        <div className="flex items-start ml-2 overflow-hidden flex-1 min-w-0">
          <FieldIcon
            id={`spv2-cz-icon-${cropZone.id}`}
            features={
              geometry ? [{ type: 'Feature', geometry, properties: {} }] : []
            }
            width={26}
            height={26}
            color={iconColor}
            border={iconBorder}
          />
          <div className="ml-2 overflow-hidden min-w-0 flex-1">
            <HighlightedText
              text={`${cropZone.name}${cropZone.archived ? ' (Archived)' : ''}`}
              searchTerm={searchTerm}
              className={`${
                isChecked ? 'font-semibold' : 'font-medium'
              } leading-5 wrap-break-word`}
              style={{ color: textColor, display: 'block' }}
              isSelected={isChecked}
              highlightColor={HIGHLIGHT_COLOR}
              selectedHighlightColor={SELECTED_HIGHLIGHT_COLOR}
            />
            <div
              className="mt-2px text-xs leading-3 flex items-center flex-wrap"
              style={{ color: metaColor }}
            >
              <span>
                <FormattedNumber roundedTo={2}>{area}</FormattedNumber> ac
                {!isCropGroupMode && cropName ? ' ·' : ''}
              </span>
              {!isCropGroupMode && cropName && (
                <>
                  <span
                    style={{ flexShrink: 0, marginLeft: 4, marginRight: 4 }}
                  >
                    <CropIconMapping
                      cropObject={{ name: cropName }}
                      size={14}
                    />
                  </span>
                  <span className="wrap-break-word">{cropName}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// FieldTitleItem
// ---------------------------------------------------------------------------
interface FieldTitleItemProps {
  field: Field;
  nodeKey: string;
  isChecked: boolean;
  isIndeterminate: boolean;
  onSelect: (field: Field) => void;
  onExpand: (key: string) => void;
  geometry?: any;
  searchTerm?: string;
  totalCzCount?: number;
  isCropGroupMode?: boolean;
}

export const FieldTitleItem: React.FC<FieldTitleItemProps> = React.memo(
  ({
    field,
    nodeKey,
    isChecked,
    isIndeterminate,
    onSelect,
    onExpand,
    geometry = null,
    searchTerm = '',
    totalCzCount,
    isCropGroupMode = false
  }) => {
    const czCount = field.cropzones.length;
    const totalAcres = field.cropzones.reduce(
      (sum, cz) => sum + getCzArea(cz),
      0
    );
    const textColor = field.archived ? ARCHIVED_COLOR : undefined;
    const metaColor = field.archived ? ARCHIVED_COLOR : META_COLOR;
    const iconColor = field.archived ? '#C2C7D0' : '#868CA2';
    const showCzCount =
      (!!searchTerm || isCropGroupMode) && totalCzCount !== undefined;
    return (
      <div
        className="spv2-title-btn"
        data-field-id={field.id}
        role="none"
        onClick={e => {
          e.stopPropagation();
          onExpand(nodeKey);
        }}
      >
        <IndeterminateCheckbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={() => onSelect(field)}
          aria-label={`Select field ${field.name}`}
        />
        <div className="flex items-start ml-2 overflow-hidden flex-1 min-w-0">
          <FieldIcon
            id={`spv2-field-icon-${field.id}`}
            features={
              geometry ? [{ type: 'Feature', geometry, properties: {} }] : []
            }
            width={26}
            height={26}
            color={iconColor}
          />
          <div className="ml-2 min-w-0 flex-1">
            <HighlightedText
              text={`${field.name}${field.archived ? ' (Archived)' : ''}`}
              searchTerm={searchTerm}
              className={`${
                isChecked ? 'font-semibold' : 'font-medium'
              } leading-5 wrap-break-word`}
              style={{ color: textColor, display: 'block' }}
              isSelected={isChecked}
              highlightColor={HIGHLIGHT_COLOR}
              selectedHighlightColor={SELECTED_HIGHLIGHT_COLOR}
            />
            <p
              className="mt-2px text-xs leading-3 whitespace-nowrap"
              style={{ color: metaColor }}
            >
              {showCzCount ? (
                <>
                  {czCount} of {totalCzCount}{' '}
                  {pluralize(totalCzCount, 'crop zone')}
                </>
              ) : (
                <>
                  {czCount} {pluralize(czCount, 'crop zone')}
                </>
              )}{' '}
              · <FormattedNumber roundedTo={2}>{totalAcres}</FormattedNumber> ac
            </p>
          </div>
        </div>
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// FarmTitleItem
// ---------------------------------------------------------------------------
interface FarmTitleItemProps {
  farm: Farm;
  nodeKey: string;
  isChecked: boolean;
  isIndeterminate: boolean;
  onSelect: (farm: Farm) => void;
  onExpand: (key: string) => void;
  searchTerm?: string;
  totalFieldCount?: number;
  isCropGroupMode?: boolean;
}

export const FarmTitleItem: React.FC<FarmTitleItemProps> = React.memo(
  ({
    farm,
    nodeKey,
    isChecked,
    isIndeterminate,
    onSelect,
    onExpand,
    searchTerm = '',
    totalFieldCount,
    isCropGroupMode = false
  }) => {
    const fieldCount = farm.fields.length;
    const totalAcres = farm.fields.reduce(
      (sum, f) => sum + f.cropzones.reduce((s, cz) => s + getCzArea(cz), 0),
      0
    );
    const textColor = farm.archived ? ARCHIVED_COLOR : undefined;
    const metaColor = farm.archived ? ARCHIVED_COLOR : META_COLOR;
    // "X of Y fields" during search and crop-group mode — same rule as the
    // field row's crop-zone count.
    const showSearchCount =
      (!!searchTerm || isCropGroupMode) && totalFieldCount !== undefined;

    return (
      <div
        className="spv2-title-btn"
        role="none"
        onClick={e => {
          e.stopPropagation();
          onExpand(nodeKey);
        }}
      >
        <IndeterminateCheckbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={() => onSelect(farm)}
          aria-label={`Select farm ${farm.name}`}
        />
        <div className="ml-2 flex-1 min-w-0">
          <HighlightedText
            text={`${farm.name}${farm.archived ? ' (Archived)' : ''}`}
            searchTerm={searchTerm}
            className={`${
              isChecked ? 'font-semibold' : 'font-medium'
            } leading-5 wrap-break-word`}
            style={{ color: textColor, display: 'block' }}
            isSelected={isChecked}
            highlightColor={HIGHLIGHT_COLOR}
            selectedHighlightColor={SELECTED_HIGHLIGHT_COLOR}
          />
          <p className="mt-2px text-xs leading-3" style={{ color: metaColor }}>
            {showSearchCount ? (
              <>
                {fieldCount} of {totalFieldCount}{' '}
                {pluralize(totalFieldCount, 'field')}
              </>
            ) : (
              <>
                {fieldCount} {pluralize(fieldCount, 'field')}
              </>
            )}{' '}
            · <FormattedNumber roundedTo={2}>{totalAcres}</FormattedNumber> ac
          </p>
        </div>
      </div>
    );
  }
);

// ---------------------------------------------------------------------------
// CropGroupTitleItem
// ---------------------------------------------------------------------------
interface CropGroupTitleItemProps {
  cropGroup: CropGroup;
  nodeKey: string;
  isChecked: boolean;
  isIndeterminate: boolean;
  onSelect: (cg: CropGroup) => void;
  onExpand: (key: string) => void;
  searchTerm?: string;
}

export const CropGroupTitleItem: React.FC<CropGroupTitleItemProps> = React.memo(
  ({
    cropGroup,
    nodeKey,
    isChecked,
    isIndeterminate,
    onSelect,
    onExpand,
    searchTerm = ''
  }) => (
    <div
      className="spv2-title-btn spv2-title-btn--single"
      role="none"
      onClick={e => {
        e.stopPropagation();
        onExpand(nodeKey);
      }}
    >
      <IndeterminateCheckbox
        checked={isChecked}
        indeterminate={isIndeterminate}
        onChange={() => onSelect(cropGroup)}
        aria-label={`Select crop ${cropGroup.name}`}
      />
      <div className="flex items-center ml-2 flex-1 min-w-0">
        <div className="shrink-0 mr-2">
          <CropIconMapping cropObject={{ name: cropGroup.name }} size={20} />
        </div>
        <HighlightedText
          text={cropGroup.name}
          searchTerm={searchTerm}
          className="font-normal text-sm leading-5 wrap-break-word"
          style={{ display: 'block', color: '#14151C' }}
          isSelected={isChecked}
          highlightColor={HIGHLIGHT_COLOR}
          selectedHighlightColor={SELECTED_HIGHLIGHT_COLOR}
        />
      </div>
    </div>
  )
);

// ---------------------------------------------------------------------------
// SelectAllRow
// ---------------------------------------------------------------------------
interface SelectAllRowProps {
  allIds: string[];
  checkedIds: string[];
  onSelectAll: (ids: string[]) => void;
  onDeselectAll: () => void;
  searchActive?: boolean;
}

export const SelectAllRow: React.FC<SelectAllRowProps> = ({
  allIds,
  checkedIds,
  onSelectAll,
  onDeselectAll,
  searchActive = false
}) => {
  const checkedIdSet = new Set(checkedIds);
  const checkedCount = allIds.filter(id => checkedIdSet.has(id)).length;
  const isChecked = allIds.length > 0 && checkedCount === allIds.length;
  const isIndeterminate = checkedCount > 0 && checkedCount < allIds.length;
  const label = searchActive
    ? 'Select all search results'
    : 'Select all properties';

  return (
    <div className="flex items-center gap-2 p-2 pr-1">
      <IndeterminateCheckbox
        checked={isChecked}
        indeterminate={isIndeterminate}
        onChange={() => (isChecked ? onDeselectAll() : onSelectAll(allIds))}
        aria-label={label}
      />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
};
